import { Box, Typography } from '@mui/material';
import { AppCheckboxWithLabel } from 'components/core/AppCheckbox/AppCheckboxWithLabel';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import Grid from 'components/core/Grid';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { UserProfileIcon } from 'components/core/UserProfileIcon/UserProfileIcon';
import { FormikAppTextField } from 'components/formik/core/FormikAppTextField/FormikAppTextField';
import { CheckoutMissingInfo } from 'components/views/pages/Checkout/CheckoutStep1/components/CheckoutMissingInfo';
import { AlluraFormikErrorMessage } from 'components/formik/core/AlluraErrorMessage/AlluraFormikErrorMessage';

import { CheckoutParamEnum } from 'components/views/pages/Checkout/types';
import { Formik, Form, yupToFormErrors } from 'formik';
import { isUserMissingAnyProfileInfo, userMissingProfileInfo } from 'helpers/userHelpers';
import { useAppParam } from 'hooks/useAppParam';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { useMemo } from 'react';
import * as yup from 'yup';
import { CheckoutSectionTitle } from '../../components/CheckoutSectionTitle';
import { CheckoutBoxWrapper } from '../../styles';
import { ChevronRight } from '@mui/icons-material';
import { getYearsDifferenceExact } from '@alluradirect/react';

const validationSchema = yup.object().shape({
  message: yup.string().min(6).required().label('Message'),
  hasFilledRequiredInfo: yup
    .boolean()
    .oneOf([true], 'Must fill in all required info')
    .required()
    .label('Filled Required Info'),
});

export const CheckoutRequiredInfo = ({ checkoutData, userData, handleNextStep, marketing }: CheckoutStepProps) => {
  const { param } = useAppParam(CheckoutParamEnum.message);
  const { setParam: setMarketingParam } = useAppParam(CheckoutParamEnum.marketing);
  const { setParam: setMessageParam } = useAppParam(CheckoutParamEnum.message);

  const isLoading = !checkoutData || !userData;

  const missingInfo = userData ? userMissingProfileInfo(userData) : undefined;
  const isMissingInfo = isUserMissingAnyProfileInfo(missingInfo);
  const hostImage = checkoutData?.BOOKING?.PROPERTY?.PRIMARY_CONTACT?.THUMBNAIL;

  const guestDOB = checkoutData?.BOOKING.GUEST.DATE_OF_BIRTH;
  const minAgeToBook = checkoutData?.BOOKING.PROPERTY.RULES.MIN_AGE_TO_BOOK;

  let buttonText = 'Continue';

  let isMinAgeToBook = true;
  let guestAge = 0;
  if (guestDOB != undefined && guestDOB.length && minAgeToBook != undefined) {
    guestAge = getYearsDifferenceExact(new Date(guestDOB), new Date());
    if (guestAge < minAgeToBook) {
      isMinAgeToBook = false;
      buttonText = `You must be at least ${minAgeToBook} years old to book this property`;
    }
  }

  let isUnderMaxBookableDays = true;
  const maxBookableDays = checkoutData?.BOOKING.PROPERTY.RULES.MAX_BOOKABLE_DAYS;
  const bookingNights = checkoutData?.BOOKING.NIGHTS;
  if (maxBookableDays != undefined && bookingNights != undefined && bookingNights > maxBookableDays) {
    isUnderMaxBookableDays = false;
    buttonText = `This property can be booked for a maximum of ${maxBookableDays} nights`;
  }


  const onSubmit = (values: any) => {
    handleNextStep({ paramName: CheckoutParamEnum.message, paramValue: values.message });
  };

  const initialValues = useMemo(() => {
    const init = {
      message: '',
      hasFilledRequiredInfo: !isMissingInfo,
    };

    if (param) {
      init.message = param as string;
    }

    return init;
  }, [isMissingInfo, param]);

  type valuesWithPromoCheck = typeof initialValues;
  const handleValidate = async (values: valuesWithPromoCheck) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
    } catch (e) {
      return yupToFormErrors(e)
    }
  }

  return (
    <>
      <Grid item xs={12}>
        <AppDivider marginVertical="16px" borderWidth='0' />
      </Grid>
      <CheckoutBoxWrapper>
        <Grid item xs={12}>
          <Formik
            initialValues={initialValues}
            validate={handleValidate}
            onSubmit={(values, { validateForm }) => {
              validateForm({ ...values, checkPromo: true })
                .then(errors => {
                  if (Object.keys(errors).length === 0) {
                    onSubmit(values)
                  }
                })
            }}
            enableReinitialize
          >
            <Form>
              {isMissingInfo ? (
                <>
                  <CheckoutMissingInfo isLoading={isLoading} user={userData} missingInfo={missingInfo} />
                  <AppDivider marginVertical="16px" borderWidth='0' />
                </>
              ) : null}

              <Grid item xs={12}>
                <LoadingSkeleton isLoading={isLoading}>
                  <CheckoutSectionTitle>Message to the host</CheckoutSectionTitle>
                </LoadingSkeleton>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} mb={2}>
                      <UserProfileIcon image={hostImage} isLoading={isLoading} size={40} />
                      <Box>
                        <LoadingSkeleton isLoading={isLoading}>
                          <Typography variant="body1">
                            Briefly let the host know about your group and why you&apos;re travelling.
                          </Typography>
                        </LoadingSkeleton>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} mt={1}>
                    <LoadingSkeleton isLoading={isLoading} scaleOneToOne>
                      <FormikAppTextField
                        name="message"
                        variant="outlined"
                        fullWidth
                        placeholder="Write your message"
                        label={'Message to the host'}
                        multiline
                        minRows={4}
                        maxRows={100}
                        onBlur={e => setMessageParam(e.target.value)}
                      />
                    </LoadingSkeleton>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <LoadingSkeleton isLoading={isLoading} scaleOneToOne>
                  <AppCheckboxWithLabel
                    checked={Boolean(Number(marketing))}
                    value={Number(marketing)}
                    onChange={() => {
                      setMarketingParam(Number(marketing) === 1 ? 0 : 1);
                    }}
                    label="Include me on member-only promotions and discounts offers."
                    labelPlacement="end"
                    formControlProps={{
                      componentsProps: {
                        typography: {
                          variant: 'caption',
                        },
                      },
                      sx: {
                        userSelect: 'none',
                      },
                    }}
                  />
                </LoadingSkeleton>
              </Grid>

              <Grid item xs={12}>
                <AppDivider marginVertical="24px" />
                <AlluraFormikErrorMessage fieldName="hasFilledRequiredInfo" />
                <LoadingButton
                  isLoading={false}
                  onClick={() => null}
                  isFullWidth
                  isFormikButton
                  text={buttonText}
                  isDisabled={isLoading || !isMinAgeToBook || !isUnderMaxBookableDays}
                  size="large"
                  endIcon={isMinAgeToBook && isUnderMaxBookableDays ? <ChevronRight /> : null}
                />
              </Grid>
            </Form>
          </Formik>
        </Grid>
      </CheckoutBoxWrapper>
    </>
  );
};
